import React from 'react';
import { ConnectShopifyEmptyState } from '../components/dashboard/ConnectShopifyEmptyState.component';
import { Spinner } from '../components/UI';
import { useCapabilities } from '../services/capabilities';
import { isHighAccess } from '../utils';
import { useLogin } from '../services/login';

interface Options {
    title?: string;
    description?: string;
    /**
     * Let brands through when they have non-Shopify orders (internal self-orders, API
     * orders) even with no store connected.
     *
     * For pages whose content is not actually Shopify-exclusive — orders and the
     * customers they create. Without this, a brand that has placed internal orders is
     * told to "connect a store to see them", which hides data it already owns.
     */
    allowNonShopifyOrders?: boolean;
}

// #region withShopifyRequired
/**
 * Wraps a Shopify-backed page so that reaching it by URL without a connected
 * store renders an explanatory empty state.
 *
 * The wrapped component is never mounted in that case, so none of its
 * Shopify-backed fetch effects fire. Staff accounts bypass the gate — they are
 * not tied to a single storefront.
 */
export function withShopifyRequired<T extends object>(
    WrappedComponent: React.FunctionComponent<T>,
    options: Options = {}
): React.FunctionComponent<T> {
    const Guarded: React.FunctionComponent<T> = (props: T): React.ReactElement => {
        const [user] = useLogin();
        const { hasShopify, hasNonShopifyOrders, resolved, load } = useCapabilities();

        React.useEffect(() => { load(); }, [load]);

        if (isHighAccess(user)) return <WrappedComponent {...props} />;

        // The atom is null for one render after a hard refresh — never flash the
        // "connect your store" panel at a brand that actually has one.
        if (!resolved) {
            return (
                <div className="flex items-center justify-center py-16">
                    <Spinner show={true} />
                </div>
            );
        }

        const allowedWithoutStore = options.allowNonShopifyOrders === true && hasNonShopifyOrders;

        if (!hasShopify && !allowedWithoutStore) {
            return <ConnectShopifyEmptyState title={options.title} description={options.description} />;
        }

        return <WrappedComponent {...props} />;
    };

    Guarded.displayName = `withShopifyRequired(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
    return Guarded;
}
